// src/utils/homepageRanker.js
// Scores and selects the best mix for your homepage.
// Keeps your existing CSS/markup intact — only changes WHICH articles go where.

export function rankAndSelect(articles = [], featured = [], userPrefs = {}) {
  const parseTime = (a) => {
    const d = new Date(a.fullDate || a.date || a.publishTimeRaw || a.publishTime);
    return isNaN(d) ? 0 : d.getTime();
  };
  const hoursAgo = (ts) => (Date.now() - ts) / 36e5;
  const cat = (a) => (a.category || "").toLowerCase();
  const hasTag = (a, tag) => {
    const tags = (a.tags || a.wpTags || []).map(t => String(t).toLowerCase());
    return tags.includes(String(tag).toLowerCase());
  };

  // Base: favor recency (last ~3 days) with a tiny boost for long reads
  const baseScore = (a) => {
    const t = parseTime(a);
    const recency = Math.max(0, 72 - hoursAgo(t)); // decay after 72h
    const isLong  = (a.content || "").length > 2000 ? 2 : 0;
    return recency + isLong;
  };

  // Entertainment boosts
  const topicalBoost = (a) => {
    let b = 0;
    if (["awards","festivals","film","tv","reviews","industry"].includes(cat(a))) b += 4;
    if (hasTag(a, "Trailer") || hasTag(a, "First Look")) b += 3;
    if (hasTag(a, "Exclusive") || hasTag(a, "Interview")) b += 3;
    return b;
  };

  // Optional light personalization
  const prefBoost = (a) => {
    const w = userPrefs[cat(a)] || 1.0;   // e.g. { tv:1.2, film:1.0 }
    return (w - 1) * 5;
  };

  const score = (a) => baseScore(a) + topicalBoost(a) + prefBoost(a);

  const byScore = [...articles].sort((a, b) => score(b) - score(a));
  const byDate  = [...articles].sort((a, b) => parseTime(b) - parseTime(a));
  const featuredSorted = [...featured].sort((a, b) => parseTime(b) - parseTime(a));

  // Deduper
  const used = new Set();
  const take = (list, n, filter = () => true) => {
    const out = [];
    for (const a of list) {
      if (out.length >= n) break;
      if (used.has(a.id)) continue;
      if (!filter(a)) continue;
      out.push(a); used.add(a.id);
    }
    return out;
  };

  // 1) Hero: editorial pick if any, else highest score
  const hero = (featuredSorted[0] || byScore[0]) || null;
  if (hero) used.add(hero.id);

  // 2) Top Stories (4), cap 2 per category
  const caps = {};
  const capOk = (a) => {
    const c = cat(a) || "misc";
    caps[c] = caps[c] || 0;
    if (caps[c] >= 2) return false;
    caps[c] += 1; return true;
  };
  const topStories = take([...featuredSorted.slice(1), ...byScore], 4, capOk);

  // 3) Latest News (15): pure recency, no dupes (total homepage limit ~20)
  const latest = take(byDate, 15);

  // 4) Rails (3 each) – available for future sections if want them
  const reviews   = take(byDate.filter(a => cat(a) === "reviews"),   3);
  const festivals = take(byDate.filter(a => cat(a) === "festivals"), 3);
  const awards    = take(byDate.filter(a => cat(a) === "awards"),    3);

  // Optional: trailers rail
  const trailers  = take(byDate.filter(a => hasTag(a, "Trailer")), 3);

  return { hero, topStories, latest, reviews, festivals, awards, trailers };
}